import { ProviderData } from "@/types/provider";
import { 
  Building2, 
  Clock, 
  Percent, 
  CheckCircle2,
  ShieldCheck,
  Wallet
} from "lucide-react";
import thornLogo from "@/assets/thorn_stor.webp";
import ucLogo from "@/assets/UC.png";
import bankTransferIcon from "@/assets/banköverföring.png";
import creditsafeImg from "@/assets/creditsafe.png";

export const thornData: ProviderData = {
  id: "thorn",
  name: "Thorn",
  slug: "thorn",
  logo: thornLogo,
  logoSize: "small",
  type: "private_loan",
  
  trustpilot: {
    rating: 4.7,
    reviews: 531,
    url: "https://se.trustpilot.com/review/thorn.se"
  },
  
  hero: {
    badges: [
      { text: "Personlig rådgivning", variant: "default" }
    ],
    description: "Thorn erbjuder privatlån med personlig service och flexibla villkor. Lån från 10 000 kr upp till 150 000 kr med möjlighet till förtidsinlösen utan extra kostnad.",
    ctaText: "Ansök hos Thorn",
    ctaButtonText: "Ansök hos Thorn",
    ctaButtonVariant: "default",
    stickyCTASubtitle: "Effektiv ränta 16,13% - 41,02%"
  },
  
  stats: [
    {
      icon: Wallet,
      value: "150 000 kr",
      label: "Maxbelopp",
      description: "Från 10 000 kr"
    },
    {
      icon: Percent,
      value: "16,13 – 41,02 %",
      label: "Effektiv ränta",
      description: "Varierar"
    },
    {
      icon: Clock,
      value: "1 – 12 år",
      label: "Löptid",
      description: "Flexibel återbetalningstid"
    },
    {
      icon: Building2,
      value: "495 kr",
      label: "Uppläggningsavgift",
      description: "Fast avgift"
    },
    {
      icon: ShieldCheck,
      value: "UC",
      label: "Kreditupplysning",
      description: "Påverkar UC-Score"
    },
    {
      icon: CheckCircle2,
      value: "Ja",
      label: "Betalningsanmärkningar",
      description: "Accepteras"
    }
  ],
  
  features: {
    pros: [
      "Återbetala hela lånet utan extra kostnad",
      "Möjlighet till personlig rådgivning",
      "Olika lån för olika situationer"
    ],
    cons: [
      "Aviavgift även för e-faktura"
    ]
  },
  
  keyInformation: [
    {
      image: ucLogo,
      title: "Kreditupplysning",
      value: "UC"
    },
    {
      image: bankTransferIcon,
      title: "Utbetalning via",
      value: "Banköverföring"
    },
    {
      icon: CheckCircle2,
      title: "Effektiv ränta",
      value: "16,13 – 41,02 %",
      description: "Varierar"
    },
    {
      image: creditsafeImg,
      title: "Medlemskap",
      value: "Creditsafe"
    }
  ],
  
  termsAndConditions: [
    {
      term: "Lånebelopp",
      value: "10 000 – 150 000 kr"
    },
    {
      term: "Nominell ränta",
      value: "14,88 – 21,6 %"
    },
    {
      term: "Effektiv ränta",
      value: "16,13 – 41,02 %"
    },
    {
      term: "Löptid",
      value: "1 – 12 år"
    },
    {
      term: "Uppläggningsavgift",
      value: "495 kr"
    },
    {
      term: "Lån utan UC",
      value: "Nej"
    },
    {
      term: "Svar på ansökan",
      value: "Samma dag"
    },
    {
      term: "Betalningslösning",
      value: "Banköverföring"
    }
  ],
  
  loanRequirements: [
    {
      requirement: "Legitimering",
      value: "Ja"
    },
    {
      requirement: "Folkbokförd i Sverige",
      value: "Ja, minst 2 år"
    },
    {
      requirement: "Svenskt personnummer",
      value: "Ja"
    },
    {
      requirement: "Svenskt mobilnummer",
      value: "Ja"
    },
    {
      requirement: "Svenskt kontonummer",
      value: "Ja"
    },
    {
      requirement: "Minsta årsinkomst",
      value: "Ja"
    },
    {
      requirement: "Betalningsanmärkning OK?",
      value: "Ja"
    },
    {
      requirement: "Ålderskrav",
      value: "24 +"
    }
  ],
  
  interestRateSection: {
    title: "Thorns ränta och avgifter",
    description: "Thorn erbjuder privatlån med transparenta villkor. Räntan sätts individuellt baserat på din kreditvärdighet och återbetalningstid.",
    rateTable: [
      {
        label: "Lånebelopp (lägsta – högsta)",
        value: "10 000 – 150 000 kr"
      },
      {
        label: "Nominell ränta",
        value: "14,88 – 21,6 %"
      },
      {
        label: "Effektiv ränta",
        value: "16,13 – 41,02 %"
      },
      {
        label: "Uppläggningsavgift",
        value: "495 kr"
      },
      {
        label: "Fakturaavgift (Pappersfaktura)",
        value: "45 kr"
      },
      {
        label: "Fakturaavgift (Autogiro)",
        value: "19 kr"
      },
      {
        label: "Förtidsinlösen",
        value: "Kostnadsfri"
      }
    ]
  },
  
  payoutSection: {
    title: "När betalas lånet ut?",
    description: "Hos Thorn betalas godkända lån ut snabbt, ofta samma dag om ansökan godkänns före klockan 10:00.",
    payoutTable: [
      {
        timeframe: "Beviljad före 10:00",
        duration: "Samma dag"
      },
      {
        timeframe: "Beviljad efter 10:00",
        duration: "Nästa bankdag"
      }
    ]
  },
  
  faq: [
    {
      question: "Accepterar Thorn betalningsanmärkningar?",
      answer: "Ja, Thorn kan i vissa fall acceptera betalningsanmärkningar om de bedömer att du har god nuvarande betalningsförmåga. Varje ansökan prövas individuellt."
    },
    {
      question: "Kan man låna utan UC via Thorn?",
      answer: "Nej, det går inte att låna pengar utan UC via Thorn. UC-kreditupplysning krävs för att bedöma din kreditvärdighet."
    },
    {
      question: "Vad får jag för ränta hos Thorn?",
      answer: "Räntan på ett privatlån hos Thorn sätts individuellt efter dina förutsättningar. Den effektiva räntan ligger mellan 16,13% och 41,02% beroende på lånebelopp, återbetalningstid och din kreditvärdighet."
    },
    {
      question: "När betalas lånet ut?",
      answer: "Om ditt lån godkänns före klockan 10:00 kan du få pengarna samma dag. Godkänns lånet efter 10:00 betalas det ut nästa bankdag."
    },
    {
      question: "Kan jag betala av lånet i förtid?",
      answer: "Ja, hos Thorn kan du återbetala hela lånet i förtid utan extra kostnad. Detta ger dig flexibilitet att bli skuldfri tidigare om din ekonomiska situation förbättras."
    },
    {
      question: "Kostar det något att ansöka om lån hos Thorn?",
      answer: "Nej, det är helt kostnadsfritt att ansöka om lån hos Thorn. Du betalar endast uppläggningsavgiften på 495 kr och ränta om lånet beviljas."
    },
    {
      question: "Vilka avgifter tillkommer hos Thorn?",
      answer: "Hos Thorn tillkommer en uppläggningsavgift på 495 kr. Därefter tillkommer en fakturaavgift på 45 kr för pappersfaktura eller 19 kr för autogiro. Förtidsinlösen är kostnadsfri."
    },
    {
      question: "Vilken ålder krävs för att låna hos Thorn?",
      answer: "För att ansöka om lån hos Thorn måste du vara minst 24 år gammal, till skillnad från de flesta andra långivare som kräver 18 år."
    },
    {
      question: "Vad krävs för att få låna hos Thorn?",
      answer: "Du måste vara minst 24 år, folkbokförd i Sverige i minst 2 år, ha svenskt personnummer, svenskt mobilnummer och svenskt kontonummer. Du behöver även legitimera dig och ha en godkänd inkomst."
    },
    {
      question: "Erbjuder Thorn personlig rådgivning?",
      answer: "Ja, Thorn erbjuder möjlighet till personlig rådgivning för att hjälpa dig hitta det lån som passar din ekonomiska situation bäst."
    }
  ],
  
  customerService: {
    description: "Thorn erbjuder kundservice via telefon och e-post för att hjälpa dig med frågor om din låneansökan.",
    phone: "020-242424",
    phoneHours: [
      "Vardagar: 08.00-17.00",
      "Lördag: Stängt",
      "Söndag: Stängt"
    ],
    email: "info@thorn.se",
    emailHours: [
      "Svarar inom 24 timmar vardagar"
    ]
  },
  
  companyInfo: {
    name: "Thorn Svenska Aktiebolag",
    orgNumber: "556136-8480",
    address: "Vretenvägen 13 SE",
    city: "171 54 Solna",
    website: "https://www.thorn.se",
    fiRegisterUrl: "https://www.fi.se/sv/vara-register/foretagsregistret/details?id=71622"
  },
  
  affiliateLink: {
    linkId: "thorn-privatlan",
    trackingSource: "provider-page",
    url: "https://go.adt228.com/t/t?a=35567344&as=2024680921&t=2&tk=1"
  },
  
  seo: {
    title: "Thorn Privatlån - Lån 10 000-150 000 kr | Svar samma dag",
    description: "Ansök om privatlån hos Thorn. Belopp 10 000-150 000 kr, effektiv ränta 16,13-41,02%. Personlig rådgivning och förtidsinlösen utan kostnad. Ansök idag!",
    keywords: [
      "thorn",
      "thorn lån",
      "privatlån",
      "snabblån",
      "personlig rådgivning",
      "thorn recension"
    ]
  },
  
  schema: {
    productName: "Thorn Privatlån",
    description: "Ansök om privatlån hos Thorn. Belopp 10 000-150 000 kr, effektiv ränta 16,13-41,02%. Personlig rådgivning och förtidsinlösen utan kostnad.",
    url: "https://klarfinans.se/sv-SE/privatlan/thorn",
    provider: "Thorn",
    interestRate: {
      min: 16.13,
      max: 41.02
    },
    minAmount: 10000,
    maxAmount: 150000,
    loanTerm: ["1 år", "12 år"],
    featureList: [
      "Lån från 10 000 kr upp till 150 000 kr",
      "Svar samma dag",
      "Flexibla återbetalningsvillkor 1-12 år",
      "Effektiv ränta 16,13 – 41,02 %",
      "Förtidsinlösen utan extra kostnad",
      "Personlig rådgivning",
      "Accepterar betalningsanmärkningar",
      "Snabb utbetalning - samma dag vid godkännande före 10:00"
    ],
    aggregateRating: {
      ratingValue: 4.7,
      reviewCount: 531
    }
  }
};
